import React from "react";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts";
import useWindowSize from "../hooks/useWindowSize";
import { Tooltip as TooltipStr} from "@mui/material";
import useToken from "../useToken";

// Tooltip personalizado
const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        const item = payload[0];
        return (
            <div style={{ backgroundColor: "#fff", border: "1px solid #ccc", padding: "8px", borderRadius: "4px", fontSize: "14px" }}>
                <strong>{item.name}</strong><br />
                {item.value.toFixed(1)}%
            </div>
        );
    }
    return null;
};

// Leyenda personalizada
const CustomLegend = ({ unidades_contadas, unidades_teoricas }) => (
    <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", justifyContent: "center" }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ width: 12, height: 12, backgroundColor: '#0088FE', marginRight: 6, borderRadius: 2 }} />
            <span style={{ fontSize: '16px', whiteSpace: 'nowrap' }}>
                Unidades Contadas: {unidades_contadas === null ? 0 : unidades_contadas.toFixed(1)}
            </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ width: 12, height: 12, backgroundColor: '#FD8080', marginRight: 6, borderRadius: 2 }} />
            <span style={{ fontSize: '16px', whiteSpace: 'nowrap' }}>
                Unidades Teóricas: {unidades_teoricas === null ? 0 : unidades_teoricas.toFixed(1)}
            </span>
        </div>
    </div>
);

const GraficoDiferenciaGeneral = ({ datos_diferencia, tipo_inv, estado_inv, loading }) => {
    const [width] = useWindowSize();
    if (loading || !datos_diferencia) {
        return <p>Cargando datos...</p>;
    }

    const porcentaje_dif_unidades = datos_diferencia.unidades_teoricas > 0
        ? (datos_diferencia.diferencia_unidades / datos_diferencia.unidades_teoricas) * 100 : 0;

    const porcentaje_dif_valorizado = datos_diferencia.imp_teorico > 0
        ? (datos_diferencia.diferencia_valorizado / datos_diferencia.imp_teorico) * 100 : 0;

    const porcentaje = datos_diferencia.unidades_teoricas > 0
        ? (datos_diferencia.unidades_contadas / datos_diferencia.unidades_teoricas) * 100 : 0;

    const data = [
        { name: "Avance Unidades", value: porcentaje, dato: datos_diferencia.unidades_contadas }
    ];

    const hora_inicio = datos_diferencia.hora_inicio;

    const height = width < 768 ? 110 : 180;
    const height_text = width < 768 ? 80 : 140;
    const outerRadius = width < 768 ? 15 : 50;
    const innerRadius = width < 768 ? 5 : 30;
    const user = useToken.getDatos();

    const COLORS = ["#0088FE"];

   const parsearFecha = (cadenaFecha) => {
        if (!cadenaFecha) return null;
        const fechaLimpia = String(cadenaFecha).replace(/\u00a0/g, ' ').trim();
        const partes = fechaLimpia.split(/\s+/);
        const fechaParte = partes[0];

        if (!fechaParte) return null;

        const [dia, mes, anio] = fechaParte.split("/").map(Number);

        return new Date(anio, mes - 1, dia);
    };

    const esFechaAntigua = (fechaStr, invStr) => {
        const fechaInicio = parsearFecha(fechaStr);
        const fechaInventario = parsearFechaInv(invStr);
        if (!fechaInicio || !fechaInventario || isNaN(fechaInicio.getTime()) || isNaN(fechaInventario.getTime())) {
            return false;
        }

        const diferenciaMilisegundos = fechaInventario - fechaInicio;
        const diferenciaDias = diferenciaMilisegundos / (1000 * 60 * 60 * 24);

        return diferenciaDias >= 7;
    };

     const esFechaFutura = (fechaStr, invStr) => {
        const fechaInicio = parsearFecha(fechaStr);
        const fechaInventario = parsearFechaInv(invStr);
        if (!fechaInicio || !fechaInventario || isNaN(fechaInicio.getTime()) || isNaN(fechaInventario.getTime())) {
            return false;
        }

        const diferenciaMilisegundos =  fechaInicio - fechaInventario;
        const diferenciaDias = diferenciaMilisegundos / (1000 * 60 * 60 * 24);

        return diferenciaDias >= 7;
    };

    const parsearFechaInv = (cadenaFecha) => {
        if (!cadenaFecha) return null;
        const match = String(cadenaFecha).match(/\d{4}-\d{2}-\d{2}/);
        if (!match) return null;

        const [anio, mes, dia] = match[0].split("-").map(Number);
        return new Date(anio, mes - 1, dia);
    };

    const fechaAntigua = esFechaAntigua(hora_inicio, user.fecha);
    const fechaFutura = esFechaFutura(hora_inicio, user.fecha);

    return (
        <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            alignItems: 'flex-start',
            padding: 10,
            gap: '30px'
        }}>
            <div style={{textAlign: 'center'}}>
                <h3 style={{margin: 8}}>Información General</h3>
                <div style={{width: '100%', height_text}}>
                    <p>Estado Inv: {estado_inv.toUpperCase()}</p>
                    <p>Tipo Inv: {tipo_inv}</p>
                    {fechaAntigua ? (
                        <TooltipStr 
                            title="La fecha de inicio es anterior a la del inventario. Se deben verificar las horas de las máquinas." 
                            placement="top"
                        >
                            <p style={{ color: "red", fontWeight: "bold" }}>
                                Inicio: {hora_inicio}
                            </p>
                        </TooltipStr>
                    ) : fechaFutura ? (
                        <TooltipStr 
                            title="La fecha de inicio es mayor a la del inventario. Se deben verificar las horas de las máquinas." 
                            placement="top"
                        >
                            <p style={{ color: "red", fontWeight: "bold" }}>
                                Inicio: {hora_inicio}
                            </p>
                        </TooltipStr>
                    ) : (
                        <p>
                            Inicio: {hora_inicio}
                        </p>
                    )
                    }
                    
                </div>
            </div>
            { tipo_inv.toUpperCase() === "CON TEÓRICO" ?
            <div style={{textAlign: 'center'}}>
                <h3 style={{margin: 8}}>Diferencias</h3>
                <div style={{width: '100%', height_text}}>
                    <p>Diferencias por Unidades: {porcentaje_dif_unidades.toFixed(1)}%</p>
                    <p>Diferencias por Valorizado: {porcentaje_dif_valorizado.toFixed(1)}%</p>
                </div>
            </div>: null}
            <div style={{textAlign: 'center'}}>
            <h3 style={{margin: 5}}>Avance Por Unidades (%)</h3>
                <div style={{width: '100%', height}}>
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={data}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={outerRadius}
                                innerRadius={innerRadius}
                            >
                                {data.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]}/>
                                ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip/>}/>
                            <Legend
                                content={() =>
                                    <CustomLegend
                                        unidades_contadas={datos_diferencia.unidades_contadas}
                                        unidades_teoricas={datos_diferencia.unidades_teoricas}
                                    />
                                }
                            />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
};

export default GraficoDiferenciaGeneral;
